import React, { CSSProperties, useState, useEffect } from 'react';
import type { User, AmbulanceType, StaffCategory } from '../services/api';
import { getCurrentStatus } from '../services/api';

export interface Ambulance {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    licensePlate: string;
    vehicleNumber?: string;
    type?: string;
    status: string;
    capacity?: number;
    mileage?: number;
    speedLimit?: number;
    currentLocation?: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    assignedPilot?: {
      userId: string;
      name: string;
    };
    notes?: string;
    tenantId?: string;
    gpsDevice?: {
      gpsDeviceId: string;
      deviceId: string;
    };
    assignedStaffs?: Array<{
      userId: string;
      name: string;
    }>;
    image?: string;
  };
}

interface AmbulanceStaffMappingProps {
  ambulances: Ambulance[];
  loading: boolean;
  getStatusChipStyle: (status: string) => CSSProperties;
  formatAmbulanceStatus: (status: string) => string;
  pilots: User[];
  otherStaff: User[];
  ambulanceTypes?: AmbulanceType[];
  staffCategories?: StaffCategory[];
  onUpdate: (ambulanceId: string, pilotId: string | null, staffIds: string[]) => Promise<void>;
}

const AmbulanceStaffMapping: React.FC<AmbulanceStaffMappingProps> = ({
  ambulances,
  loading,
  getStatusChipStyle,
  formatAmbulanceStatus,
  pilots,
  otherStaff,
  ambulanceTypes = [],
  staffCategories = [],
  onUpdate,
}) => {
  const [selectedAmbulanceId, setSelectedAmbulanceId] = useState<string | null>(null);
  const [selectedPilotId, setSelectedPilotId] = useState<string | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [deselectedStaffIds, setDeselectedStaffIds] = useState<string[]>([]); // Track staff that were explicitly deselected
  const [showConfirmDrawer, setShowConfirmDrawer] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Search states
  const [showAmbulanceSearch, setShowAmbulanceSearch] = useState(false);
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [showPilotSearch, setShowPilotSearch] = useState(false);
  const [pilotSearchTerm, setPilotSearchTerm] = useState('');
  const [showStaffSearch, setShowStaffSearch] = useState(false);
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState<Record<string, { status: string; hasActiveBooking: boolean }>>({});
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Monitor theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Helper function to get today's date bounds
  const getTodayBounds = (): { startOfDay: number; endOfDay: number } => {
    const now = Date.now();
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    const startOfDay = date.getTime();
    date.setHours(23, 59, 59, 999);
    const endOfDay = date.getTime();
    return { startOfDay, endOfDay };
  };

  // Track current date to refresh when date changes
  const [currentDateKey, setCurrentDateKey] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  });

  // Monitor date changes
  useEffect(() => {
    const checkDateChange = () => {
      const today = new Date();
      const newDateKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
      if (newDateKey !== currentDateKey) {
        setCurrentDateKey(newDateKey);
      }
    };

    // Check every minute for date changes
    const interval = setInterval(checkDateChange, 60000);
    return () => clearInterval(interval);
  }, [currentDateKey]);

  // Fetch attendance status for all pilots and staff
  useEffect(() => {
    const fetchAttendanceStatus = async () => {
      const allUsers = [...pilots, ...otherStaff];
      const statusMap: Record<string, { status: string; hasActiveBooking: boolean }> = {};
      const { startOfDay, endOfDay } = getTodayBounds();

      // Fetch status for all users in parallel
      const statusPromises = allUsers.map(async (user) => {
        const userId = user._id || user.id;
        if (!userId) return null;

        try {
          const response = await getCurrentStatus(userId);
          const attendanceData = response.data.data;
          const checkInTime = attendanceData?.checkInTime;
          const attendanceStatus = attendanceData?.status || 'checkedOut';
          
          // Check if check-in is for today
          const isCheckedInToday = checkInTime && 
            checkInTime >= startOfDay && 
            checkInTime <= endOfDay && 
            attendanceStatus === 'checkedIn';
          
          // If checked out today or not checked in today, consider as absent
          const finalStatus = isCheckedInToday ? 'checkedIn' : 'checkedOut';
          const hasActiveBooking = !!(user.spec?.activeBooking);
          
          return {
            userId,
            status: finalStatus,
            hasActiveBooking,
          };
        } catch (error) {
          // If status fetch fails, assume checked out
          const hasActiveBooking = !!(user.spec?.activeBooking);
          return {
            userId,
            status: 'checkedOut',
            hasActiveBooking,
          };
        }
      });

      const results = await Promise.all(statusPromises);
      results.forEach((result) => {
        if (result) {
          statusMap[result.userId] = {
            status: result.status,
            hasActiveBooking: result.hasActiveBooking,
          };
        }
      });

      setAttendanceStatus(statusMap);
    };

    if (pilots.length > 0 || otherStaff.length > 0) {
      fetchAttendanceStatus();
    }
  }, [pilots, otherStaff, currentDateKey]);

  const selectedAmbulance = selectedAmbulanceId
    ? ambulances.find((a) => a._id === selectedAmbulanceId)
    : null;

  // Helper function to get ambulance type display name
  const getAmbulanceTypeDisplayName = (type?: string): string => {
    if (!type) return '—';
    const ambulanceType = ambulanceTypes.find((t) => t.metadata.name === type);
    return ambulanceType?.metadata.displayName || type;
  };

  // Helper function to get staff category display name
  const getStaffCategoryDisplayName = (category?: string): string => {
    if (!category) return '—';
    const staffCategory = staffCategories.find((c) => c.metadata.name === category);
    return staffCategory?.metadata.displayName || category;
  };

  // Check if there are changes in pilot or staff assignments
  const hasChanges = () => {
    if (!selectedAmbulance) return false;

    // Check pilot change
    const currentPilotId = selectedAmbulance.spec.assignedPilot?.userId || null;
    const pilotChanged = currentPilotId !== selectedPilotId;

    // Check staff changes
    const currentStaffIds = (selectedAmbulance.spec.assignedStaffs?.map((s) => s.userId) || []).sort();
    const newStaffIds = [...selectedStaffIds].sort();
    const staffChanged = 
      currentStaffIds.length !== newStaffIds.length ||
      currentStaffIds.some((id, index) => id !== newStaffIds[index]);

    return pilotChanged || staffChanged;
  };

  // Sort ambulances by status: Available > On Duty > Off Duty > Maintenance
  const getStatusPriority = (status: string): number => {
    // Normalize status by removing underscores, hyphens, and converting to lowercase
    const statusNormalized = status.toLowerCase().replace(/[-_\s]/g, '');
    if (statusNormalized === 'available') return 1;
    if (statusNormalized === 'onduty') return 2;
    if (statusNormalized === 'offduty') return 3;
    if (statusNormalized === 'maintenance') return 4;
    return 5; // Unknown status goes to bottom
  };

  const sortedAmbulances = [...ambulances].sort((a, b) => {
    const priorityA = getStatusPriority(a.spec.status);
    const priorityB = getStatusPriority(b.spec.status);
    return priorityA - priorityB;
  });

  // Filter ambulances by name and license plate
  const filteredAmbulances = sortedAmbulances.filter((ambulance) => {
    if (!ambulanceSearchTerm.trim()) return true;
    const searchLower = ambulanceSearchTerm.toLowerCase();
    const name = ambulance.metadata.name.toLowerCase();
    const licensePlate = ambulance.spec.licensePlate.toLowerCase();
    return name.includes(searchLower) || licensePlate.includes(searchLower);
  });

  // Get user status for sorting and coloring
  const getUserStatus = (user: User): 'present' | 'presentOnTrip' | 'absent' => {
    const userId = user._id || user.id || '';
    const userStatus = attendanceStatus[userId];
    
    if (!userStatus) {
      return 'absent';
    }

    const isCheckedIn = userStatus.status === 'checkedIn';
    const hasActiveBooking = userStatus.hasActiveBooking;

    if (isCheckedIn && hasActiveBooking) {
      return 'presentOnTrip';
    } else if (isCheckedIn) {
      return 'present';
    } else {
      return 'absent';
    }
  };

  // Get user status priority for sorting (present: 1, presentOnTrip: 2, absent: 3)
  const getUserStatusPriority = (status: 'present' | 'presentOnTrip' | 'absent'): number => {
    if (status === 'present') return 1;
    if (status === 'presentOnTrip') return 2;
    return 3;
  };

  // Get color for user status
  const getStatusColor = (status: 'present' | 'presentOnTrip' | 'absent'): { border: string; background: string; opacity: number } => {
    if (status === 'present') {
      return {
        border: '#10b981', // green
        background: 'rgba(16, 185, 129, 0.2)',
        opacity: 1,
      };
    } else if (status === 'presentOnTrip') {
      return {
        border: '#ef4444', // red
        background: 'rgba(239, 68, 68, 0.2)',
        opacity: 1,
      };
    } else {
      return {
        border: '#9ca3af', // gray
        background: 'rgba(156, 163, 175, 0.15)',
        opacity: 0.6,
      };
    }
  };

  // Filter and sort pilots by name and number
  const filteredPilots = pilots
    .filter((pilot) => {
      if (!pilotSearchTerm.trim()) return true;
      const searchLower = pilotSearchTerm.toLowerCase();
      const name = (pilot.metadata?.name || pilot.name || '').toLowerCase();
      const phone = (pilot.spec?.phone || pilot.phone || '').toLowerCase();
      return name.includes(searchLower) || phone.includes(searchLower);
    })
    .sort((a, b) => {
      const statusA = getUserStatus(a);
      const statusB = getUserStatus(b);
      const priorityA = getUserStatusPriority(statusA);
      const priorityB = getUserStatusPriority(statusB);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // If same status, sort by name
      const nameA = (a.metadata?.name || a.name || '').toLowerCase();
      const nameB = (b.metadata?.name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Filter and sort staff by name and number
  const filteredStaff = otherStaff
    .filter((staff) => {
      if (!staffSearchTerm.trim()) return true;
      const searchLower = staffSearchTerm.toLowerCase();
      const name = (staff.metadata?.name || staff.name || '').toLowerCase();
      const phone = (staff.spec?.phone || staff.phone || '').toLowerCase();
      return name.includes(searchLower) || phone.includes(searchLower);
    })
    .sort((a, b) => {
      const statusA = getUserStatus(a);
      const statusB = getUserStatus(b);
      const priorityA = getUserStatusPriority(statusA);
      const priorityB = getUserStatusPriority(statusB);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // If same status, sort by name
      const nameA = (a.metadata?.name || a.name || '').toLowerCase();
      const nameB = (b.metadata?.name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Calculate ambulance counts by status
  const ambulanceCounts = filteredAmbulances.reduce((acc, ambulance) => {
    const status = ambulance.spec.status?.toLowerCase().replace(/[-_\s]/g, '') || 'unknown';
    if (status === 'available') acc.available = (acc.available || 0) + 1;
    else if (status === 'onduty') acc.onDuty = (acc.onDuty || 0) + 1;
    else if (status === 'offduty') acc.offDuty = (acc.offDuty || 0) + 1;
    else if (status === 'maintenance') acc.maintenance = (acc.maintenance || 0) + 1;
    else acc.unknown = (acc.unknown || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Calculate pilot counts by status
  const pilotCounts = filteredPilots.reduce((acc, pilot) => {
    const status = getUserStatus(pilot);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Calculate staff counts by status
  const staffCounts = filteredStaff.reduce((acc, staff) => {
    const status = getUserStatus(staff);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleAmbulanceClick = (ambulance: Ambulance) => {
    // If clicking on the already selected ambulance, deselect it
    if (selectedAmbulanceId === ambulance._id) {
      setSelectedAmbulanceId(null);
      setSelectedPilotId(null);
      setSelectedStaffIds([]);
      setDeselectedStaffIds([]);
    } else {
      // Select the new ambulance
      setSelectedAmbulanceId(ambulance._id);
      setSelectedPilotId(ambulance.spec.assignedPilot?.userId || null);
      // Start with assigned staff selected by default (so they're included in update)
      setSelectedStaffIds(ambulance.spec.assignedStaffs?.map((s) => s.userId) || []);
      setDeselectedStaffIds([]); // Reset deselected staff when selecting a new ambulance
    }
  };

  const handlePilotClick = (pilotId: string) => {
    // Single selection - clicking same pilot deselects, clicking different pilot selects it
    // Only one pilot can be selected at a time
    if (selectedPilotId === pilotId) {
      setSelectedPilotId(null);
    } else {
      setSelectedPilotId(pilotId);
    }
  };

  const handleStaffClick = (staffId: string) => {
    const isOriginallyAssigned = selectedAmbulance?.spec.assignedStaffs?.some(
      (s) => s.userId === staffId
    ) || false;
    
    if (selectedStaffIds.includes(staffId)) {
      // Remove from selection
      setSelectedStaffIds(selectedStaffIds.filter((id) => id !== staffId));
      // If it was originally assigned, mark it as deselected to remove highlight
      if (isOriginallyAssigned && !deselectedStaffIds.includes(staffId)) {
        setDeselectedStaffIds([...deselectedStaffIds, staffId]);
      }
    } else {
      // Add to selection
      setSelectedStaffIds([...selectedStaffIds, staffId]);
      // Remove from deselected list if it was there (user is re-selecting it)
      if (deselectedStaffIds.includes(staffId)) {
        setDeselectedStaffIds(deselectedStaffIds.filter((id) => id !== staffId));
      }
    }
  };

  const handleUpdate = async () => {
    if (!selectedAmbulanceId) return;
    
    setIsUpdating(true);
    try {
      await onUpdate(selectedAmbulanceId, selectedPilotId, selectedStaffIds);
      setShowConfirmDrawer(false);
      // Optionally reset selection or refresh
    } catch (error) {
      console.error('Failed to update ambulance:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const renderUserItem = (user: User, isHighlighted: boolean = false, isSelectable: boolean = false, onSelect?: () => void) => {
    const name = user.metadata?.name || user.name;
    const category = user.spec?.staffCategory;

    const phone = user.spec?.phone || user.phone || '—';
    const categoryLabel = category ? getStaffCategoryDisplayName(category) : '—';

    const userId = user._id || user.id || '';
    const isSelected = isSelectable && selectedStaffIds.includes(userId);
    const userStatus = getUserStatus(user);
    const statusColor = getStatusColor(userStatus);

    return (
      <div
        key={userId}
        onClick={isSelectable && onSelect ? onSelect : undefined}
        className={isHighlighted || isSelected ? 'mapping-item-selected' : 'mapping-item-border'}
        style={{
          padding: '12px 14px',
          borderRadius: '10px',
          border: isHighlighted || isSelected
            ? `2px solid ${isDarkTheme ? '#94a3b8' : '#667eea'}`
            : `1px solid ${statusColor.border}`,
          marginBottom: '10px',
          fontSize: '15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          background: isHighlighted || isSelected
            ? (isDarkTheme ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)')
            : statusColor.background,
          cursor: isSelectable && onSelect ? 'pointer' : 'default',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isHighlighted || isSelected 
            ? (isDarkTheme ? '0 4px 12px rgba(148, 163, 184, 0.3)' : '0 4px 12px rgba(102, 126, 234, 0.25)') 
            : '0 2px 4px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          opacity: statusColor.opacity,
        }}
        onMouseEnter={(e) => {
          if (isSelectable && onSelect) {
            if (!isHighlighted && !isSelected) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            } else {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
            }
          }
        }}
        onMouseLeave={(e) => {
          if (isSelectable && onSelect) {
            e.currentTarget.style.transform = 'translateY(0)';
            if (!isHighlighted && !isSelected) {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            } else {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
            }
          }
        }}
      >
        {(isHighlighted || isSelected) && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: isDarkTheme ? '#94a3b8' : '#667eea',
            zIndex: 2,
          }} />
        )}
        {!(isHighlighted || isSelected) && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: statusColor.border,
            zIndex: 1,
          }} />
        )}
        <div style={{ fontWeight: 600, fontSize: '16px', width: '50%', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '4px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-item-name">
          {name}
        </div>
        <div style={{ fontSize: '14px', width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, padding: '0 4px', gap: '2px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-item-secondary">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {phone}
          </div>
          {category && (
            <div style={{ fontSize: '13px', opacity: 0.7, whiteSpace: 'nowrap' }}>
              {categoryLabel}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="card"
      style={{
        height: 'calc(100vh - 215px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div className="card-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading">Loading data...</div>
        ) : ambulances.length === 0 ? (
          <div className="empty-state">
            <h3>No ambulances yet</h3>
            <p>Create your first ambulance to configure staff mapping</p>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr',
                gap: '32px',
                alignItems: 'stretch',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
            {/* Ambulance list column */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${isDarkTheme ? '#94a3b8' : '#cbd5e1'}`,
              }}>
                <h3 className="card-title" style={{ margin: 0, fontSize: '19px', fontWeight: 700 , paddingBottom: 0}}>
                  Ambulances
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {ambulanceCounts.available > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${ambulanceCounts.available} Ambulance${ambulanceCounts.available !== 1 ? 's' : ''} ${ambulanceCounts.available === 1 ? 'is' : 'are'} Available`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {ambulanceCounts.available}
                    </span>
                  )}
                  {ambulanceCounts.onDuty > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${ambulanceCounts.onDuty} Ambulance${ambulanceCounts.onDuty !== 1 ? 's' : ''} ${ambulanceCounts.onDuty === 1 ? 'is' : 'are'} On Duty`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {ambulanceCounts.onDuty}
                    </span>
                  )}
                  {ambulanceCounts.offDuty > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${ambulanceCounts.offDuty} Ambulance${ambulanceCounts.offDuty !== 1 ? 's' : ''} ${ambulanceCounts.offDuty === 1 ? 'is' : 'are'} Off Duty`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(245, 158, 11, 0.2)',
                        color: '#f59e0b',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {ambulanceCounts.offDuty}
                    </span>
                  )}
                  {ambulanceCounts.maintenance > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${ambulanceCounts.maintenance} Ambulance${ambulanceCounts.maintenance !== 1 ? 's' : ''} ${ambulanceCounts.maintenance === 1 ? 'is' : 'are'} in Maintenance`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(100, 116, 139, 0.2)',
                        color: '#64748b',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {ambulanceCounts.maintenance}
                    </span>
                  )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {showAmbulanceSearch && (
                    <input
                      type="text"
                      placeholder="Search by name or license plate..."
                      value={ambulanceSearchTerm}
                      onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid',
                        fontSize: '14px',
                        width: '220px',
                        flex: '0 0 220px',
                        outline: 'none',
                        backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      }}
                      className="mapping-item-border"
                      autoFocus
                    />
                  )}
                  <button
                    onClick={() => {
                      setShowAmbulanceSearch(!showAmbulanceSearch);
                      setAmbulanceSearchTerm('');
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isDarkTheme ? '#cbd5e1' : '#475569',
                    }}
                    title={showAmbulanceSearch ? "Close search" : "Search"}
                  >
                    {showAmbulanceSearch ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                minHeight: 0,
                maxHeight: '100%',
                paddingRight: '4px',
              }}>
                {filteredAmbulances.map((ambulance) => {
                  const statusChipStyle = getStatusChipStyle(ambulance.spec.status);
                  const isSelected = selectedAmbulanceId === ambulance._id;
                  const statusColor = statusChipStyle.color || '#64748b';
                  // Convert hex color with appended alpha to rgba format
                  // Format is like #10b9810D where 0D is the alpha (13/255 ≈ 0.05)
                  // Increase alpha to make background darker (0.2 instead of 0.05)
                  let statusBackground = statusChipStyle.backgroundColor || `${statusColor}1A`;
                  if (statusBackground.length === 9 && statusBackground.startsWith('#')) {
                    const r = parseInt(statusBackground.slice(1, 3), 16);
                    const g = parseInt(statusBackground.slice(3, 5), 16);
                    const b = parseInt(statusBackground.slice(5, 7), 16);
                    // Use 0.2 opacity instead of the original low opacity for darker background
                    statusBackground = `rgba(${r}, ${g}, ${b}, 0.2)`;
                  } else if (!statusBackground.includes('rgba') && !statusBackground.includes('rgb')) {
                    // If it's a simple hex color, convert to rgba with 0.2 opacity
                    const hex = statusColor.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    statusBackground = `rgba(${r}, ${g}, ${b}, 0.2)`;
                  }
                  return (
                    <div
                      key={ambulance._id}
                      onClick={() => handleAmbulanceClick(ambulance)}
                      className={isSelected ? 'mapping-item-selected' : 'mapping-item-border'}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '10px',
                        border: isSelected
                          ? `2px solid ${isDarkTheme ? '#94a3b8' : '#667eea'}`
                          : `1px solid ${statusColor}`,
                        marginBottom: '10px',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: isSelected
                          ? isDarkTheme ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)'
                          : statusBackground,
                        background: isSelected
                          ? isDarkTheme ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)'
                          : statusBackground,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isSelected 
                          ? (isDarkTheme ? '0 4px 12px rgba(148, 163, 184, 0.3)' : '0 4px 12px rgba(102, 126, 234, 0.25)') 
                          : '0 2px 4px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        } else {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = isDarkTheme ? '0 6px 16px rgba(148, 163, 184, 0.55)' : '0 6px 16px rgba(102, 126, 234, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        if (!isSelected) {
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                        } else {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
                        }
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: isDarkTheme ? '#94a3b8' : '#667eea',
                          zIndex: 2,
                        }} />
                      )}
                      {!isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: statusColor,
                          zIndex: 1,
                        }} />
                      )}
                      <div style={{ fontWeight: 600, fontSize: '16px', width: '50%', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '4px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-item-name">
                        {ambulance.metadata.name}
                      </div>
                      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, padding: '0 4px', gap: '4px' }}>
                        <code style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          background: isDarkTheme ? 'rgba(148, 163, 184, 0.15)' : 'rgba(102, 126, 234, 0.1)',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          fontWeight: 600,
                          border: `1px solid ${isDarkTheme ? 'rgba(148, 163, 184, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                          whiteSpace: 'nowrap',
                          color: isDarkTheme ? '#ffffff' : undefined,
                        }}>
                          {ambulance.spec.licensePlate}
                        </code>
                        {ambulance.spec.type && (
                          <div style={{
                            fontSize: '13px',
                            color: isDarkTheme ? '#ffffff' : undefined,
                            opacity: 0.7,
                            whiteSpace: 'nowrap',
                          }}>
                            {getAmbulanceTypeDisplayName(ambulance.spec.type)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pilot list column */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${isDarkTheme ? '#94a3b8' : '#cbd5e1'}`,
              }}>
                <h3 className="card-title" style={{ margin: 0, fontSize: '19px', fontWeight: 700, paddingBottom: 0 }}>
                  Pilots
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {pilotCounts.present > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${pilotCounts.present} Pilot${pilotCounts.present !== 1 ? 's' : ''} ${pilotCounts.present === 1 ? 'is' : 'are'} Present`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {pilotCounts.present}
                    </span>
                  )}
                  {pilotCounts.presentOnTrip > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${pilotCounts.presentOnTrip} Pilot${pilotCounts.presentOnTrip !== 1 ? 's' : ''} ${pilotCounts.presentOnTrip === 1 ? 'is' : 'are'} On Trip`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {pilotCounts.presentOnTrip}
                    </span>
                  )}
                  {pilotCounts.absent > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${pilotCounts.absent} Pilot${pilotCounts.absent !== 1 ? 's' : ''} ${pilotCounts.absent === 1 ? 'is' : 'are'} Absent`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(156, 163, 175, 0.2)',
                        color: '#9ca3af',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {pilotCounts.absent}
                    </span>
                  )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {showPilotSearch && (
                    <input
                      type="text"
                      placeholder="Search by name or number..."
                      value={pilotSearchTerm}
                      onChange={(e) => setPilotSearchTerm(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid',
                        fontSize: '14px',
                        width: '220px',
                        flex: '0 0 220px',
                        outline: 'none',
                        backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      }}
                      className="mapping-item-border"
                      autoFocus
                    />
                  )}
                  <button
                    onClick={() => {
                      setShowPilotSearch(!showPilotSearch);
                      setPilotSearchTerm('');
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isDarkTheme ? '#cbd5e1' : '#475569',
                    }}
                    title={showPilotSearch ? "Close search" : "Search"}
                  >
                    {showPilotSearch ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                minHeight: 0,
                maxHeight: '100%',
                paddingRight: '4px',
              }}>
                {filteredPilots.length === 0 ? (
                  <div style={{ fontSize: '15px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-empty-message">No pilots found</div>
                ) : (
                  filteredPilots.map((pilot) => {
                    const pilotId = pilot._id || pilot.id || '';
                    // Only show originally assigned pilot as highlighted if no new selection has been made
                    const originallyAssigned = selectedAmbulance?.spec.assignedPilot?.userId === pilotId;
                    const isSelected = selectedPilotId === pilotId;
                    // Highlight only if: (originally assigned AND no selection made) OR currently selected
                    const shouldHighlight = (originallyAssigned && selectedPilotId === null) || isSelected;
                    const userStatus = getUserStatus(pilot);
                    const statusColor = getStatusColor(userStatus);
                    return (
                      <div
                        key={pilotId}
                        onClick={() => handlePilotClick(pilotId)}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '10px',
                          border: shouldHighlight
                            ? `2px solid ${isDarkTheme ? '#94a3b8' : '#667eea'}`
                            : `1px solid ${statusColor.border}`,
                          marginBottom: '10px',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: shouldHighlight
                            ? isDarkTheme ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)'
                            : statusColor.background,
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: shouldHighlight 
                            ? (isDarkTheme ? '0 4px 12px rgba(148, 163, 184, 0.3)' : '0 4px 12px rgba(102, 126, 234, 0.25)') 
                            : '0 2px 4px rgba(0, 0, 0, 0.05)',
                          position: 'relative',
                          overflow: 'hidden',
                          opacity: statusColor.opacity,
                        }}
                        className={shouldHighlight ? 'mapping-item-selected' : 'mapping-item-border'}
                        onMouseEnter={(e) => {
                          if (!shouldHighlight) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                            const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
                            e.currentTarget.style.backgroundColor = isDark
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)';
                          } else {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = isDarkTheme ? '0 6px 16px rgba(148, 163, 184, 0.55)' : '0 6px 16px rgba(102, 126, 234, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          if (!shouldHighlight) {
                            e.currentTarget.style.backgroundColor = statusColor.background;
                            e.currentTarget.style.background = statusColor.background;
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          } else {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
                          }
                        }}
                      >
                        {shouldHighlight && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: isDarkTheme ? '#94a3b8' : '#667eea',
                            zIndex: 2,
                          }} />
                        )}
                        {!shouldHighlight && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: statusColor.border,
                            zIndex: 1,
                          }} />
                        )}
                        <div style={{ fontWeight: 600, fontSize: '16px', width: '50%', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '4px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-item-name">
                          {pilot.metadata?.name || pilot.name}
                        </div>
                        <div style={{ fontSize: '14px', width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, padding: '0 4px', gap: '2px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-item-secondary">
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pilot.spec?.phone || pilot.phone || '—'}
                          </div>
                          {pilot.spec?.staffCategory && (
                            <div style={{ fontSize: '13px', opacity: 0.7, whiteSpace: 'nowrap' }}>
                              {getStaffCategoryDisplayName(pilot.spec.staffCategory)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Other staff list column */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${isDarkTheme ? '#94a3b8' : '#cbd5e1'}`,
              }}>
                <h3 className="card-title" style={{ margin: 0, fontSize: '19px', fontWeight: 700, paddingBottom: 0 }}>
                  Other Staff
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {staffCounts.present > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${staffCounts.present} Staff${staffCounts.present !== 1 ? '(s)' : ''} ${staffCounts.present === 1 ? 'is' : 'are'} Present`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {staffCounts.present}
                    </span>
                  )}
                  {staffCounts.presentOnTrip > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${staffCounts.presentOnTrip} Staff${staffCounts.presentOnTrip !== 1 ? '(s)' : ''} ${staffCounts.presentOnTrip === 1 ? 'is' : 'are'} On Trip`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {staffCounts.presentOnTrip}
                    </span>
                  )}
                  {staffCounts.absent > 0 && (
                    <span 
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          text: `${staffCounts.absent} Staff${staffCounts.absent !== 1 ? '(s)' : ''} ${staffCounts.absent === 1 ? 'is' : 'are'} Absent`,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 5,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      style={{ 
                        fontSize: '12px', 
                        padding: '3px 6px',
                        borderRadius: '10px',
                        background: 'rgba(156, 163, 175, 0.2)',
                        color: '#9ca3af',
                        fontWeight: 600,
                        cursor: 'help',
                      }}>
                      {staffCounts.absent}
                    </span>
                  )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {showStaffSearch && (
                    <input
                      type="text"
                      placeholder="Search by name or number..."
                      value={staffSearchTerm}
                      onChange={(e) => setStaffSearchTerm(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid',
                        fontSize: '14px',
                        width: '220px',
                        flex: '0 0 220px',
                        outline: 'none',
                        backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                        color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      }}
                      className="mapping-item-border"
                      autoFocus
                    />
                  )}
                  <button
                    onClick={() => {
                      setShowStaffSearch(!showStaffSearch);
                      setStaffSearchTerm('');
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isDarkTheme ? '#cbd5e1' : '#475569',
                    }}
                    title={showStaffSearch ? "Close search" : "Search"}
                  >
                    {showStaffSearch ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                minHeight: 0,
                maxHeight: '100%',
                paddingRight: '4px',
              }}>
                {filteredStaff.length === 0 ? (
                  <div style={{ fontSize: '15px', color: isDarkTheme ? '#ffffff' : undefined }} className="mapping-empty-message">No other staff found</div>
                ) : (
                  filteredStaff.map((staff) => {
                    const staffId = staff._id || staff.id || '';
                    const isOriginallyAssigned = selectedAmbulance?.spec.assignedStaffs?.some(
                      (s) => s.userId === staffId
                    ) || false;
                    const isDeselected = deselectedStaffIds.includes(staffId);
                    // Highlight if originally assigned AND not explicitly deselected
                    // When user clicks on a highlighted staff, it gets deselected and highlight is removed
                    const shouldHighlight = isOriginallyAssigned && !isDeselected;
                    return renderUserItem(staff, shouldHighlight, true, () => handleStaffClick(staffId));
                  })
                )}
              </div>
            </div>
          </div>
          {selectedAmbulanceId && (
            <div className="mapping-update-section" style={{ 
              marginTop: '20px', 
              paddingTop: '20px',
              borderTop: `2px solid ${isDarkTheme ? '#94a3b8' : '#cbd5e1'}`,
              width: '100%',
            }}>
              <button
                onClick={() => setShowConfirmDrawer(true)}
                disabled={!hasChanges()}
                className="btn-submit"
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '17px',
                  fontWeight: 600,
                  cursor: hasChanges() ? 'pointer' : 'not-allowed',
                  opacity: hasChanges() ? 1 : 0.5,
                  borderRadius: '10px',
                  border: 'none',
                  background: hasChanges() 
                    ? (isDarkTheme ? '#667eea' : '#667eea')
                    : (isDarkTheme ? '#475569' : '#cbd5e1'),
                  color: 'white',
                  boxShadow: hasChanges() 
                    ? (isDarkTheme ? '0 4px 15px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)')
                    : (isDarkTheme ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (hasChanges()) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = isDarkTheme ? '0 6px 20px rgba(102, 126, 234, 0.5)' : '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = hasChanges() 
                    ? (isDarkTheme ? '0 4px 15px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)')
                    : (isDarkTheme ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)');
                }}
              >
                Update
              </button>
            </div>
          )}
        </>
        )}
      </div>

      {/* Confirmation Drawer */}
      {showConfirmDrawer && selectedAmbulance && (
        <div className="drawer-overlay" onClick={() => setShowConfirmDrawer(false)}>
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">Staff Assignment Update</h3>
              <button className="drawer-close" onClick={() => setShowConfirmDrawer(false)}>
                ×
              </button>
            </div>

            <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div className="mapping-confirmation-message" style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                fontSize: '20px',
                flexShrink: 0,
                marginBottom: '20px',
                fontWeight: 600,
                color: isDarkTheme ? '#ffffff' : undefined,
              }}>
                Are you sure you want to update the staff assignment for this ambulance? This will replace the current assignment.
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                paddingRight: '8px',
              }}>
                <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>Ambulance Details</h4>
                <div className="mapping-detail-box" style={{ padding: '12px', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Name:</strong> {selectedAmbulance.metadata.name}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <strong>License Plate:</strong> <code>{selectedAmbulance.spec.licensePlate}</code>
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong>Status:</strong>{' '}
                      <span
                        style={{
                          fontSize: '14px',
                          padding: '3px 6px',
                          borderRadius: '4px',
                          fontWeight: '500',
                          display: 'inline-block',
                          ...getStatusChipStyle(selectedAmbulance.spec.status),
                        }}
                      >
                        {formatAmbulanceStatus(selectedAmbulance.spec.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>Current Assignment</h4>
                <div className="mapping-current-assignment" style={{ padding: '12px', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Pilot:</strong>
                    {selectedAmbulance.spec.assignedPilot ? (
                      <div style={{ marginTop: '4px', marginLeft: '0' }}>
                        {(() => {
                          const pilot = pilots.find((p) => (p._id || p.id) === selectedAmbulance.spec.assignedPilot?.userId);
                          const name = selectedAmbulance.spec.assignedPilot.name;
                          const phone = pilot?.spec?.phone || pilot?.phone;
                          return (
                            <div style={{ marginBottom: '4px' }}>
                              {name}{phone ? ` (${phone})` : ''}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      ' Not assigned'
                    )}
                  </div>
                  <div>
                    <strong>Staff:</strong>
                    {selectedAmbulance.spec.assignedStaffs && selectedAmbulance.spec.assignedStaffs.length > 0 ? (
                      <div style={{ marginTop: '4px', marginLeft: '0' }}>
                        {selectedAmbulance.spec.assignedStaffs.map((s, index) => {
                          const staff = otherStaff.find((st) => (st._id || st.id) === s.userId);
                          const phone = staff?.spec?.phone || staff?.phone;
                          return (
                            <div key={index} style={{ marginBottom: '4px' }}>
                              {s.name}{phone ? ` (${phone})` : ''}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      ' Not assigned'
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>New Assignment</h4>
                <div className="mapping-new-assignment" style={{ padding: '12px', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Pilot:</strong>
                    {selectedPilotId ? (
                      <div style={{ marginTop: '4px', marginLeft: '0' }}>
                        {(() => {
                          const pilot = pilots.find((p) => (p._id || p.id) === selectedPilotId);
                          const name = pilot?.metadata?.name || pilot?.name || 'Unknown';
                          const phone = pilot?.spec?.phone || pilot?.phone;
                          return (
                            <div style={{ marginBottom: '4px' }}>
                              {name}{phone ? ` (${phone})` : ''}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      ' Not assigned'
                    )}
                  </div>
                  <div>
                    <strong>Staff:</strong>
                    {selectedStaffIds.length > 0 ? (
                      <div style={{ marginTop: '4px', marginLeft: '0' }}>
                        {selectedStaffIds.map((id, index) => {
                          const staff = otherStaff.find((s) => (s._id || s.id) === id);
                          const name = staff?.metadata?.name || staff?.name || 'Unknown';
                          const phone = staff?.spec?.phone || staff?.phone;
                          return (
                            <div key={index} style={{ marginBottom: '4px' }}>
                              {name}{phone ? ` (${phone})` : ''}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      ' Not assigned'
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowConfirmDrawer(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)',
            backgroundColor: isDarkTheme ? '#1e293b' : '#0f172a',
            color: '#ffffff',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            zIndex: 10000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {tooltip.text}
          <div
            style={{
              position: 'absolute',
              bottom: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: `5px solid ${isDarkTheme ? '#1e293b' : '#0f172a'}`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AmbulanceStaffMapping;


